import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material'
import { useMemo, useState } from 'react'

import { COMBOBOX_FILTER_COMPACT, HIDE_ON_MOBILE } from '@/theme/responsive'

import { useGetProcurementReferenceRowsQuery } from '../../store/api/procurementReferenceApi'
import type { ProcurementFilters } from '../ProcurementPage'

import ProcurementReferenceGrid from './ProcurementReferenceGrid'
import {
  PROCUREMENT_REFERENCE_PAGE_CONFIGS,
  PROCUREMENT_REFERENCE_PAGE_ORDER,
  type ProcurementReferencePageKey,
} from './procurementReferenceConfig'

interface Props {
  // Shared "Inbound Logistics" (Purchase Control) filter owned by ProcurementPage,
  // passed down to every tab. Only ContractStatistics/PendingPayment consume it.
  filters: ProcurementFilters
}

export default function ProcurementReferencesView({ filters }: Props) {
  const [pageKey, setPageKey] = useState<ProcurementReferencePageKey>('ContractStatistics')
  const [searchByPage, setSearchByPage] = useState<Partial<Record<ProcurementReferencePageKey, string>>>({})

  const page = PROCUREMENT_REFERENCE_PAGE_CONFIGS[pageKey]
  const search = searchByPage[pageKey] ?? ''

  // ContractStatistics/PendingPayment share the "Inbound Logistics" filter (the
  // header date + forced-close filter used by every other Purchase Control tab,
  // owned by ProcurementPage). The other 14 reference pages are unaffected — these
  // args are only forwarded to the query for these 2 keys.
  const usesSharedDateFilter = pageKey === 'ContractStatistics' || pageKey === 'PendingPayment'

  const queryArg = useMemo(
    () => ({
      pageKey,
      limit: page.limit ?? 1_000_000,
      ...(usesSharedDateFilter
        ? {
            startDate: filters.startDate || undefined,
            endDate: filters.endDate || undefined,
            isForcedClose:
              filters.isForcedClose === 'yes'
                ? true
                : filters.isForcedClose === 'no'
                  ? false
                  : undefined,
          }
        : {}),
    }),
    [page.limit, pageKey, usesSharedDateFilter, filters.startDate, filters.endDate, filters.isForcedClose],
  )

  // refetchOnMountOrArgChange: vào lại tab References phải load dữ liệu mới nhất.
  // Dùng `currentData` + `isFetching` (KHÔNG `data`/`isLoading`): khi đổi page
  // (pageKey), currentData tự về undefined + isFetching=true → grid hiện loading
  // tới khi data của ĐÚNG page về mới render (không show rows page cũ/màn trống).
  const { currentData, isFetching, error, refetch } = useGetProcurementReferenceRowsQuery(queryArg, {
    refetchOnMountOrArgChange: true,
    // Same guard as good-allocation: skip while the date range is inverted.
    skip: usesSharedDateFilter && !!filters.startDate && !!filters.endDate && filters.startDate > filters.endDate,
  })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, flex: 1 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 0.25 }}
      >
        <Box sx={HIDE_ON_MOBILE}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem', lineHeight: 1.4 }}>
            Inbound Logistics References
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Access switchboard parity for the 16 Inbound Logistics reference pages.
          </Typography>
        </Box>
        <Stack alignItems="flex-end" spacing={0.5} sx={{ ml: 'auto' }}>
          {(() => {
            // Skill /convert-form: combo → Autocomplete (anti-pattern #12 cấm
            // <TextField select>). PageKey là code internal nên dropdown chỉ
            // display label (Access `cbPreferencesList` value).
            const pageOptions = PROCUREMENT_REFERENCE_PAGE_ORDER.map((key) => ({
              value: key,
              label: PROCUREMENT_REFERENCE_PAGE_CONFIGS[key].label,
            }))
            const selectedOption = pageOptions.find((o) => o.value === pageKey) ?? undefined
            return (
              <Autocomplete
                size="small"
                options={pageOptions}
                value={selectedOption}
                onChange={(_, v) => v && setPageKey(v.value)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                disableClearable
                handleHomeEndKeys
                autoHighlight
                selectOnFocus
                sx={COMBOBOX_FILTER_COMPACT}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Go to Page:"
                    InputLabelProps={{ shrink: true }}
                    // readOnly trên <input>: mobile KHÔNG bung keyboard khi tap.
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            )
          })()}
        </Stack>
      </Stack>

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <ProcurementReferenceGrid
          page={page}
          fillHeight
          rows={currentData?.items ?? []}
          search={search}
          onSearchChange={(value) => setSearchByPage((prev) => ({ ...prev, [pageKey]: value }))}
          loading={isFetching}
          error={error}
          onRetry={() => {
            void refetch()
          }}
        />
      </Box>
    </Box>
  )
}
